import React, { useEffect, useState, useContext, ChangeEvent } from "react";
import { RouteComponentProps } from "react-router";
import { FormattedMessage } from "react-intl-phraseapp";
import { Alert, ArrowIcon, Dropdown } from "@cmsgov/ds-medicare-gov";
import { sortBy } from "sort-by-typescript";
import MedigapPolicyCard from "./MedigapPolicyCard";
import { useTranslate, sendTealiumEvent } from "../../../helpers";
import { AppPage, PrintButton, LoadingMask } from "../../../components";
import routes from "../../../app/routes";
import { AppContext } from "../../../app/store";
import {
  getMedigapPlanByType,
  MedigapPlanType,
  MedigapPlanPolicy,
  MedigapPlan,
  getMedigapPricingYear,
} from "../utils/plans";
import { getMedigapPlanPolicies } from "../../../api";
import { useFlags } from "launchdarkly-react-client-sdk";
import GetMoreAccuratePrice from "../components/GetMoreAccuratePrice";
enum MedigapPoliciesSortOption {
  COMPANY_NAME = "company_name",
  MONTHLY_PREMIUM = "monthly_premium",
  RATE_TYPE = "rate_type",
}

const MedigapPoliciesPage: React.FunctionComponent<RouteComponentProps> = ({
  match,
}) => {
  const t = useTranslate();
  const [policies, setPolicies] = useState<MedigapPlanPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [medigapPlan, setMedigapPlan] = useState<MedigapPlan | undefined>(
    undefined
  );
  const {
    state: { county, zipcode = "", language, demographicInfo },
  } = useContext(AppContext);
  const flags = useFlags();
  const {
    feEnableNextYearMedigapPricing,
    mctCurrentPlanYear,
    tmpFeEnableMedigapPolicyPricing,
  } = flags;
  const tealiumEventAction = "Step 2: Pick your policy";
  const [sortByOption, setSortByOption] = useState<MedigapPoliciesSortOption>(
    MedigapPoliciesSortOption.COMPANY_NAME
  );
  const sortPolicies = (
    policiesToSort: MedigapPlanPolicy[],
    sortOn: MedigapPoliciesSortOption
  ): MedigapPlanPolicy[] => {
    if (sortOn === MedigapPoliciesSortOption.COMPANY_NAME) {
      return [...policiesToSort].sort(sortBy("company"));
    } else if (sortOn === MedigapPoliciesSortOption.RATE_TYPE) {
      return [...policiesToSort].sort(sortBy("rate_type", "company"));
    } else if (sortOn === MedigapPoliciesSortOption.MONTHLY_PREMIUM) {
      return [...policiesToSort].sort(
        sortBy("monthly_rate_min", "company", (_k: string, v: number) =>
          Math.round(v)
        )
      );
    }
    return policiesToSort;
  };

  const fetchMedigapPolicies = async (): Promise<void> => {
    const stateAbbrev = county?.state || "";
    const { medigapPlanType } = match.params as {
      medigapPlanType: MedigapPlanType;
    };
    setMedigapPlan(
      getMedigapPlanByType({
        planType: medigapPlanType,
        pricingYear: getMedigapPricingYear({
          feEnableNextYearMedigapPricing,
          mctCurrentPlanYear,
        }),
      })
    );

    try {
      setLoading(true);
      const medigapPolicies = await getMedigapPlanPolicies(
        stateAbbrev,
        zipcode,
        medigapPlanType,
        demographicInfo
      );
      setPolicies(sortPolicies(medigapPolicies, sortByOption));
      setLoading(false);
      setError("");
    } catch (err) {
      setLoading(false);
      setError(t("medigap.api_error"));
    }
  };

  useEffect(() => {
    sendTealiumEvent(tealiumEventAction, "UI Option: Sort by company");
  }, []);

  useEffect(() => {
    setPolicies(sortPolicies(policies, sortByOption));
  }, [sortByOption]);

  useEffect(() => {
    if (zipcode && county) {
      fetchMedigapPolicies();
    }
  }, [
    location.hash,
    zipcode,
    county,
    demographicInfo,
    feEnableNextYearMedigapPricing,
    mctCurrentPlanYear,
  ]);

  const title = medigapPlan ? t(medigapPlan.plan_policy_name) : "";
  let sortOptions = [
    {
      label: t("medigap.policies.company_name_sort"),
      value: MedigapPoliciesSortOption.COMPANY_NAME,
    },
    {
      label: t("medigap.plan_results.monthly_premium_low_high_sort"),
      value: MedigapPoliciesSortOption.MONTHLY_PREMIUM,
    },
    {
      label: t("medigap.policies.rate_type_sort"),
      value: MedigapPoliciesSortOption.RATE_TYPE,
    },
  ];

  if (!tmpFeEnableMedigapPolicyPricing) {
    sortOptions = sortOptions.filter(
      x => x.value !== MedigapPoliciesSortOption.MONTHLY_PREMIUM
    );
  }

  return (
    <AppPage title={title} hideLogin>
      <LoadingMask loading={loading} delay={500} />
      <div className="ds-u-margin-top--2 ds-l-row ds-u-justify-content--between mct-hide-print">
        <a
          href={`#${routes.medigap.plans}?lang=${language}`}
          onClick={(): void => {
            sendTealiumEvent(tealiumEventAction, "Back to Medigap plans");
          }}
          className="ds-c-button ds-c-button--transparent mct-hide-print e2e-back-to-plans"
        >
          <ArrowIcon
            direction="left"
            className="ds-u-valign--middle ds-u-margin-right--1"
          />
          {t("medigap.plan_details.go_back")}
        </a>

        <PrintButton
          tealiumProps={{
            event_action: tealiumEventAction,
            event_label: "Print",
          }}
        />
      </div>
      <div className="MedigapPlans">
        <div className="ds-u-font-weight--semibold">{t("medigap.step_2")}</div>
        <h1>{title}</h1>
        {error && (
          <Alert variation="error" className="ds-u-margin-top--4">
            {error}
          </Alert>
        )}
        {policies && !loading && (
          <>
            {tmpFeEnableMedigapPolicyPricing && (
              <GetMoreAccuratePrice eventAction={tealiumEventAction} />
            )}
            <div className="ds-u-md-display--flex ds-u-justify-content--between ds-u-align-items--center ds-u-margin-y--4">
              <FormattedMessage
                id="medigap.num_policies_in_state"
                values={{ count: policies.length }}
              />
              <Dropdown
                label={t("medigap.plan_results.sort_by")}
                name="sort-policies-by"
                className="ds-u-display--flex ds-u-align-items--center ds-u-margin-top--2 ds-u-md-margin-top--0 mct-hide-print"
                labelClassName="MedigapPlans__sortSelectLabel"
                fieldClassName="e2e-sort-options"
                value={sortByOption}
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  sendTealiumEvent(
                    tealiumEventAction,
                    `UI Option: Sort by ${e.currentTarget.value}`
                  );
                  setSortByOption(
                    e.currentTarget.value as MedigapPoliciesSortOption
                  );
                }}
                options={sortOptions}
              />
            </div>
            {/* the redundant roles are a fix for a safari VO bug */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ol className="ds-c-list--bare medigap-policies" role="list">
              {policies.map(policy => {
                return (
                  // eslint-disable-next-line jsx-a11y/no-redundant-roles
                  <li key={policy.company} role="listitem">
                    <MedigapPolicyCard policy={policy} />
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </AppPage>
  );
};

export default MedigapPoliciesPage;
